import styled, { css } from 'styled-components';
import { header3, text2, Text } from '@ardoq/typography';
import { colors, r4, r8, s24, s32, s4, s8 } from '@ardoq/design-tokens';
import { IconName, ICON_SELECTOR } from '@ardoq/icons';
import { MetaDataReference, TripleOptions } from 'viewpointBuilder/types';
import { StatusType, Tag } from '@ardoq/status-ui';
import { ReactNode } from 'react';
import { FlexBox, Stack } from '@ardoq/layout';
import {
  EntityIcon,
  representationDataToEntityIconProps,
} from '@ardoq/basic-components';
import { IconSize } from '@ardoq/icons';
import { TextOverflow } from '@ardoq/popovers';

export const SidePanelSection = styled(FlexBox)`
  border-radius: ${r8};
  border: 1px solid ${colors.borderNeutral};
  background-color: ${colors.surfaceDefaultSubtle};
`;

export const Section = styled(SidePanelSection).attrs({
  flexDirection: 'column',
  gap: 'none',
  padding: 'small',
  paddingLeft: 'none',
  paddingRight: 'none',
})``;

export const List = styled.ul`
  list-style: none;
  display: flex;
  padding: 0;
  margin: 0;
  ${text2};
  gap: ${s8};
  flex-direction: column;
`;

export const ListItem = styled.li<{
  $isClickable: boolean;
  $isDisabled?: boolean;
}>`
  display: flex;
  flex-direction: row;
  gap: ${s8};
  border-radius: ${r4};
  opacity: ${props => (props.$isDisabled ? 0.5 : 1)};
  ${ICON_SELECTOR} {
    margin-right: 0;
  }

  &:hover {
    ${props =>
      props.$isClickable
        ? css`
            background: ${colors.surfaceHover};
            cursor: pointer;
          `
        : ''}
  }
`;

export const WorkspaceAndFieldItem = styled(ListItem)`
  background-color: ${colors.surfaceDefault};
  border-radius: ${s4};
  border: 1px solid ${colors.borderNeutral};
  padding: 0 ${s8};
  height: ${s24};
  margin: ${s4} 0;
  justify-content: space-between;
`;

export const FieldType = styled.span`
  text-transform: uppercase;
  color: ${colors.textSubtle};
`;

export const Label = styled.div`
  white-space: nowrap;
  max-width: 200px;
  min-width: 48px;
  align-items: center;
  display: flex;
`;

export const CountWrapper = styled.div`
  min-width: 1ch;
  justify-content: center;
  display: flex;
`;

export const EnforceContentWidth = styled.div`
  /*
  Topic is horizontal scrolling (which ideally shouldn't happen).
  Couldn't get the main content container to stretch to the width of the
  content. It would scroll horizontally, but only to the very right edge of
  the content, not adding the padding of the container. It's a bit a detail,
  but looks a bit bad without the right padding. Tried several approach, but
  could only get it to work with display table. The only purpose of the
  display table (which will add a virtual row and a virtual cell) is to force
  the container to take the width of the content.
  */
  display: table;
  width: 100%;
  height: 100%;
`;

export const TopRightCornerButtonContainer = styled.div`
  position: absolute;
  top: ${s32};
  right: ${s32};
`;

export const DoqContainer = styled(FlexBox).attrs({
  height: 'full',
  align: 'center',
  justify: 'center',
})``;

export const SidebarHeaderTitle = styled.span`
  ${header3};
  color: ${colors.textDefault};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** The subject of the panel, on its own line under the title. */
const SelectedComponentTypeHeading = ({
  selectedType,
}: {
  selectedType: TripleOptions['outgoing'][0]['metaDataComponent'];
}) => (
  <FlexBox gap="small" align="center" overflow="hidden">
    <IconBox
      align="center"
      justify="center"
      borderRadius="r4"
      flexShrink={0}
      $borderColor={selectedType.representationData.shadedColor}
      $backgroundColor={selectedType.representationData.lightenedColor}
    >
      <EntityIcon
        {...representationDataToEntityIconProps({
          ...selectedType.representationData,
          iconSize: IconSize.SMALL,
          color: selectedType.representationData.contrastColor,
        })}
      />
    </IconBox>
    <TextOverflow>
      <SidebarHeaderTitle>{selectedType.label}</SidebarHeaderTitle>
    </TextOverflow>
  </FlexBox>
);

// What `FlexBox` can't express: a fixed 24px box, and colours that come from
// the component type's representation data rather than the design tokens its
// colour props accept.
const IconBox = styled(FlexBox)<{
  $borderColor: string;
  $backgroundColor: string;
}>`
  width: ${s24};
  height: ${s24};
  border: 1px solid ${props => props.$borderColor};
  background-color: ${props => props.$backgroundColor};
`;

type SelectedType =
  | {
      type: 'component';
      selectedType: TripleOptions['outgoing'][0]['metaDataComponent'];
    }
  | {
      type: 'reference';
      selectedType: MetaDataReference;
    };

type SidebarHeaderProps = {
  headerText: string;
  rightContent?: ReactNode;
} & SelectedType;

export const SidebarHeader = ({
  type,
  selectedType,
  headerText,
  rightContent,
}: SidebarHeaderProps) => {
  return (
    <FlexBox align="start" gap="small" padding="medium" width="full">
      <Stack gap="xsmall" flex={1}>
        <Text variant="text1">{headerText}</Text>
        {type === 'reference' ? (
          <Tag
            // This isn't a warning, but StatusType.WARNING gives us the styling we want
            statusType={StatusType.WARNING}
            label={selectedType?.representationData?.displayLabel ?? ''}
            iconName={IconName.REFERENCE}
          />
        ) : (
          <SelectedComponentTypeHeading selectedType={selectedType} />
        )}
      </Stack>
      {rightContent ? <FlexBox align="center">{rightContent}</FlexBox> : null}
    </FlexBox>
  );
};

export const IconWrapper = styled.span<{
  $borderColor: string;
  $backgroundColor: string;
}>`
  background-color: ${props => props.$backgroundColor};
  border: 1px solid ${props => props.$borderColor};
  border-radius: ${r4};
  padding-left: ${s4};
  padding-right: ${s4};
  align-items: center;
  height: ${s24};
  display: flex;
  box-shadow:
    0 0 0 2px ${colors.surfaceDefault},
    0 0 0 4px ${colors.borderFocus};
`;
